<script setup>
import { onMounted, onBeforeUnmount, ref, onBeforeMount, inject, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import placeholderImg from '@/assets/images/placeholder_img.png'

const IS_MOBILE_APP = inject('IS_MOBILE_APP')
const restoStore = useRestoGLobalStore()
const cartStore = useCartGLobalStore()
const authStore = useAuthGlobalStore()
const router = useRouter()
const dynamicContent = restoStore?.restoInfo?.dynamic_site_content
const googleAnalyticsKey = restoStore?.SITE_SETTINGS?.google_analytics_key
const isLoggedIn = computed(() => authStore?.isLoggedIn)
const date = new Date()
const currentYear = date.getFullYear()
const bgColor1 = ref('#f97316')
const activeCategory = ref('All')
const isCartOpen = ref(false)

const isGiftVoucherCategory = (category) => {
  const categoryName = category?.category_name?.toLowerCase?.() || ''
  return categoryName === 'gift voucher' || categoryName === 'gift vouchers'
}

const categoryEmoji = (name = '') => {
  const lower = name.toLowerCase()
  if (lower.includes('burger')) return '🍔'
  if (lower.includes('pizza')) return '🍕'
  if (lower.includes('sushi') || lower.includes('roll')) return '🍣'
  if (lower.includes('salad')) return '🥗'
  if (lower.includes('dessert') || lower.includes('sweet') || lower.includes('cake')) return '🍰'
  if (lower.includes('drink') || lower.includes('beverage')) return '🥤'
  if (lower.includes('chicken')) return '🍗'
  if (lower.includes('kebab') || lower.includes('grill')) return '🥙'
  if (lower.includes('starter') || lower.includes('appetizer')) return '🥗'
  if (lower.includes('curry') || lower.includes('indian')) return '🍛'
  return '🍽️'
}

const menuCategories = computed(() => {
  const categories = restoStore?.MENU_DATA?.categories || []
  return categories.filter((category) => category && !isGiftVoucherCategory(category))
})

const categories = computed(() => {
  const list = [{ name: 'All', emoji: '🍽️', id: 'all' }]
  for (const category of menuCategories.value) {
    list.push({
      id: category.category_id,
      name: category.category_name,
      emoji: categoryEmoji(category.category_name)
    })
  }
  return list
})

const activeOffers = computed(() => {
  if (!IS_MOBILE_APP && dynamicContent?.info?.active_website_offers?.length > 0) {
    return dynamicContent.info.active_website_offers
  }
  if (IS_MOBILE_APP && dynamicContent?.info?.active_mobileapp_offers?.length > 0) {
    return dynamicContent.info.active_mobileapp_offers
  }
  return []
})

const offers = computed(() => {
  return activeOffers.value.map((offer, index) => ({
    title: typeof offer === 'string' ? offer : offer?.title || `Special Offer ${index + 1}`,
    description: typeof offer === 'string' ? 'Limited time deal' : offer?.description || '',
    code: typeof offer === 'string' ? `DEAL${index + 1}` : offer?.code || `DEAL${index + 1}`,
    image: placeholderImg
  }))
})

const categoryNameById = computed(() => {
  const map = {}
  for (const category of menuCategories.value) {
    map[category.category_id] = category.category_name
  }
  return map
})

const popularDishes = computed(() => {
  const products = restoStore?.restoInfo?.popular_menu?.products || []
  return products
    .filter((product) => product)
    .map((product) => {
      const availableItem = (product.items || []).find((item) => item?.is_available != 0)
      const fallbackItem = product.items?.[0]
      const item = availableItem || fallbackItem
      const price = Number(item?.price || 0)
      return {
        id: product.product_id,
        name: product.product_name || item?.item_name_full || item?.item_name,
        category:
          product.category_name || categoryNameById.value[product.category_id] || '',
        categoryId: product.category_id,
        description: product.info || item?.info || '',
        price,
        rating: '4.8',
        image: product.menu_img_url || item?.menu_img_url || placeholderImg
      }
    })
})

const dishesFromMenu = computed(() => {
  const dishes = []
  for (const category of menuCategories.value) {
    for (const product of category?.products || []) {
      if (!product) continue
      const availableItem = (product.items || []).find((item) => item?.is_available != 0)
      const fallbackItem = product.items?.[0]
      const item = availableItem || fallbackItem
      const price = Number(item?.price || 0)
      dishes.push({
        id: product.product_id,
        name: product.product_name || item?.item_name_full || item?.item_name,
        category: category.category_name,
        categoryId: category.category_id,
        description: product.info || item?.info || '',
        price,
        rating: '4.8',
        image: product.menu_img_url || item?.menu_img_url || placeholderImg
      })
    }
  }
  return dishes
})

const displayDishes = computed(() => {
  if (popularDishes.value.length) return popularDishes.value
  return dishesFromMenu.value.slice(0, 8)
})

const filteredDishes = computed(() => {
  if (activeCategory.value === 'All') return displayDishes.value
  return displayDishes.value.filter((dish) => dish.category === activeCategory.value)
})

const cartCount = computed(() => Number(cartStore?.CART_DATA?.order_total_item_quantity || 0))

const restaurantName = computed(() => dynamicContent?.info?.name || restoStore?.restoInfo?.name || '')
const restaurantAddress = computed(
  () => dynamicContent?.info?.address || restoStore?.restoInfo?.address || ''
)
const restaurantPhone = computed(() => dynamicContent?.info?.phone_number || '')
const restaurantLogo = computed(() => dynamicContent?.info?.logo || placeholderImg)
const heroImage = computed(
  () =>
    dynamicContent?.content?.rsc_slider_mobile_background_image_url ||
    placeholderImg
)
const openingHoursText = computed(() => {
  const consolidated = dynamicContent?.info?.resto_opening_times?.consolidated || []
  if (!consolidated.length) return ''
  return consolidated
    .map((row) => {
      const times = (row.am_pm_times || []).join(', ')
      return `${row.days}: ${times}`
    })
    .join(' · ')
})

const primaryOffer = computed(() => activeOffers.value[0] || null)

const goToMenu = () => {
  if (restoStore?.MENU_URL) {
    router.push({ name: 'menu', params: { menu_slug: restoStore.MENU_URL } })
  }
}

const goToCart = () => {
  router.push({ name: 'view_mobile_cart' })
}

const goToLogin = () => {
  router.push({ name: 'login' })
}

const addToCart = () => {
  goToMenu()
}

const toggleCart = () => {
  isCartOpen.value = !isCartOpen.value
  document.body.style.overflow = isCartOpen.value ? 'hidden' : 'auto'
}

const closeCart = () => {
  isCartOpen.value = false
  document.body.style.overflow = 'auto'
}

const loadThemeStyle = async (styleLoader) => {
  try {
    await styleLoader()
  } catch (error) {
    window.dispatchEvent(new Event('vite:preloadError'))
  }
}

const loadPhosphorIcons = () => {
  if (document.querySelector('script[data-phosphor]')) return
  const script = document.createElement('script')
  script.src = 'https://unpkg.com/@phosphor-icons/web'
  script.setAttribute('data-phosphor', 'true')
  document.head.appendChild(script)
}

onBeforeMount(() => {
  if (dynamicContent?.mobile_style == 1) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template3/css/style1.css'))
    bgColor1.value = '#f97316'
  } else if (dynamicContent?.mobile_style == 2) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template3/css/style2.css'))
    bgColor1.value = '#ce1212'
  } else if (dynamicContent?.mobile_style == 3) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template3/css/style3.css'))
    bgColor1.value = '#df6853'
  } else if (dynamicContent?.mobile_style == 4) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template3/css/style4.css'))
    bgColor1.value = '#ffa323'
  } else {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template3/css/style1.css'))
    bgColor1.value = '#f97316'
  }
})

onMounted(() => {
  loadPhosphorIcons()

  const favicon = document.querySelector('link[rel="icon"]')
  if (favicon) {
    favicon.href = `${restaurantLogo.value}`
  } else {
    const newFavicon = document.createElement('link')
    newFavicon.rel = 'icon'
    newFavicon.type = 'image/x-icon'
    newFavicon.href = `${restaurantLogo.value}`
    document.head.appendChild(newFavicon)
  }

  if (googleAnalyticsKey) {
    const script = document.createElement('script')
    script.src = `https://www.googletagmanager.com/gtm.js?id=${googleAnalyticsKey}`
    document.head.appendChild(script)
    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${googleAnalyticsKey}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
    document.head.appendChild(noscript)
  }
})

onBeforeUnmount(() => {
  document.body.style.overflow = 'auto'
})
</script>

<template>
  <div
    class="temp-w3 relative min-h-screen flex flex-col max-w-screen-xl mx-auto md:shadow-soft bg-white overflow-hidden text-slate-800 antialiased selection:bg-brand-500 selection:text-white"
    :style="{ '--brand-active': bgColor1 }"
  >
    <!-- Desktop/Tablet Header -->
    <header
      class="hidden md:flex items-center justify-between px-8 py-5 border-b border-slate-100 bg-white sticky top-0 z-40"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-floating overflow-hidden"
          :style="{ backgroundColor: bgColor1 }"
        >
          <img
            v-if="restaurantLogo"
            :src="restaurantLogo"
            alt="Logo"
            class="w-full h-full object-cover"
          />
          <i v-else class="ph-bold ph-fork-knife text-xl"></i>
        </div>
        <h1 class="text-2xl font-bold tracking-tight text-slate-900">
          {{ restaurantName }}
        </h1>
      </div>

      <nav class="flex items-center gap-8 font-medium text-slate-500">
        <a href="#home" class="transition-colors" :style="{ color: bgColor1 }">Home</a>
        <a href="#menu" class="hover:opacity-80 transition-colors">Menu</a>
        <a href="#offers" class="hover:opacity-80 transition-colors">Offers</a>
        <a href="#about" class="hover:opacity-80 transition-colors">About Us</a>
      </nav>

      <div class="flex items-center gap-4">
        <button
          class="relative p-2 text-slate-600 transition-colors"
          :style="{ color: cartCount > 0 ? bgColor1 : undefined }"
          @click="toggleCart"
        >
          <i class="ph ph-shopping-bag text-2xl"></i>
          <span
            v-if="cartCount > 0"
            class="absolute top-0 right-0 w-5 h-5 text-white text-xs font-bold flex items-center justify-center rounded-full border-2 border-white shadow-sm"
            :style="{ backgroundColor: bgColor1 }"
          >
            {{ cartCount }}
          </span>
        </button>
        <router-link v-if="isLoggedIn"
          class="bg-slate-900 text-white px-6 py-2.5 rounded-full font-semibold transition-colors shadow-md hover:opacity-90"
          :style="{ '--tw-shadow-color': bgColor1 }"
          :to="{ name: 'my_orders' }"
        >
          My Orders
        </router-link>
        <router-link v-else
          :to="{ name: 'login' }"
          class="bg-slate-900 text-white px-6 py-2.5 rounded-full font-semibold transition-colors shadow-md hover:opacity-90"
          :style="{ '--tw-shadow-color': bgColor1 }"
        >
          Login
        </router-link>
      </div>
    </header>

    <!-- Mobile Header -->
    <header
      class="md:hidden flex items-center justify-between px-5 py-4 bg-white/90 backdrop-blur-md sticky top-0 z-40 border-b border-slate-100 pt-safe"
    >
      <div class="min-w-0">
        <p
          class="text-xs font-semibold uppercase tracking-wider mb-0.5"
          :style="{ color: bgColor1 }"
        >
          Delivering to
        </p>
        <div class="flex items-center gap-1 cursor-pointer min-w-0">
          <h2 class="text-sm font-bold text-slate-800 truncate max-w-[14rem]">
            {{ restaurantName || 'Home' }}
            <span v-if="restaurantAddress" class="font-medium text-slate-500">
              - {{ restaurantAddress }}
            </span>
          </h2>
          <i class="ph-bold ph-caret-down text-slate-400 shrink-0"></i>
        </div>
      </div>
      <div
        class="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden border border-slate-200 shrink-0"
      >
        <img :src="restaurantLogo" alt="Logo" class="w-full h-full object-cover" />
      </div>
    </header>

    <!-- Main Content -->
    <main id="home" class="flex-1 pb-24 md:pb-12 animate-fade-in">
      <!-- Hero -->
      <section class="px-5 py-6 md:px-8 md:py-10">
        <div class="relative w-full h-48 md:h-80 rounded-3xl overflow-hidden shadow-soft group">
          <img
            :src="heroImage"
            :alt="restaurantName"
            class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div
            class="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center"
          >
            <div class="px-6 md:px-12 text-white max-w-md">
              <span
                v-if="primaryOffer"
                class="inline-block px-3 py-1 text-xs font-bold rounded-full mb-3 backdrop-blur-sm animate-slide-up text-white"
                :style="{ backgroundColor: bgColor1, animationDelay: '0.1s', opacity: 0.95 }"
              >
                SPECIAL OFFER
              </span>
              <h2
                class="text-3xl md:text-5xl font-bold leading-tight mb-2 animate-slide-up"
                style="animation-delay: 0.2s"
              >
                {{ restaurantName || 'Savor the Best Flavors' }}
              </h2>
              <p
                class="text-sm md:text-base text-slate-200 mb-4 animate-slide-up line-clamp-2"
                style="animation-delay: 0.3s"
              >
                {{
                  primaryOffer ||
                  'Order your favourite meals online and enjoy fresh flavours delivered fast.'
                }}
              </p>
              <button
                v-if="restoStore?.MENU_URL"
                class="text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-all shadow-floating active:scale-95 animate-slide-up"
                :style="{ backgroundColor: bgColor1, animationDelay: '0.4s' }"
                @click="goToMenu"
              >
                Order Now
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Search → menu -->
      <!-- <section class="px-5 md:px-8 mb-6">
        <button
          type="button"
          class="relative flex items-center w-full h-12 rounded-2xl bg-slate-100 px-4 transition-all text-left"
          @click="goToMenu"
        >
          <i class="ph ph-magnifying-glass text-slate-400 text-xl"></i>
          <span class="w-full bg-transparent border-none outline-none px-3 text-sm font-medium text-slate-400">
            Search for dishes, restaurants...
          </span>
          <span
            class="w-8 h-8 flex items-center justify-center bg-white rounded-xl shadow-sm"
            :style="{ color: bgColor1 }"
          >
            <i class="ph ph-faders"></i>
          </span>
        </button>
      </section> -->

      <!-- Categories -->
      <section id="menu" class="mb-8">
        <div class="flex items-center justify-between px-5 md:px-8 mb-4">
          <h3 class="text-lg md:text-xl font-bold text-slate-800">Categories</h3>
          <button
            v-if="restoStore?.MENU_URL"
            type="button"
            class="text-sm font-semibold"
            :style="{ color: bgColor1 }"
            @click="goToMenu"
          >
            See All
          </button>
        </div>
        <div class="flex gap-4 px-5 md:px-8 overflow-x-auto no-scrollbar pb-2 pt-1">
          <div
            v-for="(cat, index) in categories"
            :key="cat.id || cat.name"
            class="flex flex-col items-center justify-center min-w-[80px] h-[100px] rounded-[24px] cursor-pointer transition-all duration-300 shadow-sm"
            :class="
              activeCategory === cat.name
                ? 'text-white shadow-floating scale-105'
                : 'bg-white text-slate-600 border border-slate-100 hover:bg-slate-50'
            "
            :style="[
              `animation: slideUp 0.5s ease forwards ${index * 0.1}s; opacity: 0;`,
              activeCategory === cat.name ? { backgroundColor: bgColor1 } : {}
            ]"
            @click="activeCategory = cat.name"
          >
            <div
              class="w-12 h-12 rounded-full flex items-center justify-center text-2xl mb-2"
              :class="activeCategory === cat.name ? 'bg-white/20' : 'bg-slate-100'"
            >
              {{ cat.emoji }}
            </div>
            <span class="text-xs font-semibold text-center px-1 line-clamp-2">{{ cat.name }}</span>
          </div>
        </div>
      </section>

      <!-- Special Offers -->
      <section v-if="offers.length" id="offers" class="mb-10">
        <h3 class="text-lg md:text-xl font-bold text-slate-800 px-5 md:px-8 mb-4">Special Offers</h3>
        <div class="flex gap-5 px-5 md:px-8 overflow-x-auto no-scrollbar pb-4">
          <div
            v-for="(offer, index) in offers"
            :key="index"
            class="min-w-[280px] md:min-w-[320px] bg-white rounded-3xl p-1 shadow-soft border border-slate-100 relative overflow-hidden group"
          >
            <div
              class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
              :style="{ backgroundColor: `${bgColor1}0d` }"
            ></div>
            <div class="flex gap-4 items-center p-3">
              <div class="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 relative">
                <img :src="offer.image" :alt="offer.title" class="w-full h-full object-cover" />
                <div
                  class="absolute top-0 left-0 text-white text-[10px] font-bold px-2 py-0.5 rounded-br-lg z-10"
                  :style="{ backgroundColor: bgColor1 }"
                >
                  %
                </div>
              </div>
              <div>
                <h4 class="font-bold text-slate-800 text-sm md:text-base leading-tight mb-1 line-clamp-2">
                  {{ offer.title }}
                </h4>
                <p class="text-xs text-slate-500 mb-2">{{ offer.description }}</p>
                <div class="flex items-center justify-between">
                  <span
                    class="inline-block bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded-md border border-dashed border-slate-300"
                  >
                    {{ offer.code }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Popular Dishes -->
      <section class="px-5 md:px-8 mb-10">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg md:text-xl font-bold text-slate-800">Popular Dishes</h3>
          <div class="flex gap-2">
            <button
              type="button"
              class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              @click="goToMenu"
            >
              <i class="ph-bold ph-caret-left"></i>
            </button>
            <button
              type="button"
              class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              @click="goToMenu"
            >
              <i class="ph-bold ph-caret-right"></i>
            </button>
          </div>
        </div>

        <transition-group
          name="list"
          tag="div"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          <div
            v-for="dish in filteredDishes"
            :key="dish.id"
            class="bg-white rounded-3xl p-4 shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center gap-4 group hover:shadow-soft transition-all duration-300"
          >
            <div
              class="relative w-full md:w-32 h-40 md:h-32 rounded-2xl overflow-hidden flex-shrink-0"
            >
              <img
                :src="dish.image"
                :alt="dish.name"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div
                class="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1 shadow-sm"
              >
                <i class="ph-fill ph-star text-yellow-400 text-xs"></i>
                <span class="text-xs font-bold text-slate-700">{{ dish.rating }}</span>
              </div>
            </div>
            <div class="flex-1 flex flex-col justify-between h-full">
              <div>
                <div class="flex justify-between items-start mb-1">
                  <h4 class="font-bold text-slate-800 text-base">{{ dish.name }}</h4>
                  <button type="button" class="text-slate-300 hover:text-red-500 transition-colors">
                    <i class="ph-fill ph-heart text-xl"></i>
                  </button>
                </div>
                <p class="text-xs text-slate-500 line-clamp-2 mb-3">{{ dish.description }}</p>
              </div>
              <div class="flex items-center justify-between mt-auto">
                <div class="flex flex-col">
                  <span
                    v-if="dish.price"
                    class="text-xs text-slate-400 line-through font-medium mb-0.5"
                  >
                    £{{ (dish.price * 1.2).toFixed(2) }}
                  </span>
                  <span
                    class="font-extrabold text-lg"
                    :style="{ color: bgColor1 }"
                  >
                    £{{ Number(dish.price || 0).toFixed(2) }}
                  </span>
                </div>
                <button
                  type="button"
                  class="w-10 h-10 bg-slate-900 text-white rounded-2xl flex items-center justify-center transition-colors shadow-md active:scale-90"
                  @click="addToCart(dish)"
                  @mouseenter="($event) => ($event.currentTarget.style.backgroundColor = bgColor1)"
                  @mouseleave="($event) => ($event.currentTarget.style.backgroundColor = '#0f172a')"
                >
                  <i class="ph-bold ph-plus text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </transition-group>
        <div v-if="filteredDishes.length === 0" class="py-10 text-center text-slate-500 text-sm">
          No dishes found in this category.
        </div>
      </section>

      <!-- About / Footer card -->
      <section id="about" class="px-5 md:px-8 mb-8">
        <div class="bg-slate-900 rounded-3xl p-6 md:p-10 text-white relative overflow-hidden">
          <div
            class="absolute top-[-50%] right-[-10%] w-64 h-64 rounded-full blur-[80px] opacity-40 pointer-events-none"
            :style="{ backgroundColor: bgColor1 }"
          ></div>

          <div class="flex flex-col md:flex-row gap-8 relative z-10">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-4">
                <div
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-white overflow-hidden"
                  :style="{ backgroundColor: bgColor1 }"
                >
                  <img
                    v-if="restaurantLogo"
                    :src="restaurantLogo"
                    alt=""
                    class="w-full h-full object-cover"
                  />
                  <i v-else class="ph-bold ph-fork-knife text-sm"></i>
                </div>
                <h2 class="text-xl font-bold tracking-tight text-white">
                  {{ restaurantName }}
                </h2>
              </div>
              <p
                v-if="dynamicContent?.content?.rsc_about_us_text_1"
                class="text-slate-300 text-sm mb-6 leading-relaxed line-clamp-4"
                v-html="dynamicContent?.content?.rsc_about_us_text_1"
              ></p>
              <p v-else class="text-slate-300 text-sm mb-6 leading-relaxed">
                Experience the finest culinary delights delivered straight to your door. Fresh
                ingredients, masterful recipes, lightning-fast delivery.
              </p>

              <div class="space-y-3">
                <div v-if="restaurantAddress" class="flex items-center gap-3 text-sm text-slate-300">
                  <div
                    class="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"
                    :style="{ color: bgColor1 }"
                  >
                    <i class="ph-fill ph-map-pin"></i>
                  </div>
                  <span>{{ restaurantAddress }}</span>
                </div>
                <div v-if="openingHoursText" class="flex items-center gap-3 text-sm text-slate-300">
                  <div
                    class="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"
                    :style="{ color: bgColor1 }"
                  >
                    <i class="ph-fill ph-clock"></i>
                  </div>
                  <span class="line-clamp-2">{{ openingHoursText }}</span>
                </div>
                <div v-if="restaurantPhone" class="flex items-center gap-3 text-sm text-slate-300">
                  <div
                    class="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"
                    :style="{ color: bgColor1 }"
                  >
                    <i class="ph-fill ph-phone"></i>
                  </div>
                  <a :href="`tel:${restaurantPhone}`" class="hover:opacity-80">{{
                    restaurantPhone
                  }}</a>
                </div>
              </div>
            </div>

            <div
              class="flex flex-col justify-end md:items-end border-t border-slate-700 md:border-none pt-6 md:pt-0"
            >
              <p class="text-xs text-slate-400 mb-4">
                &copy; {{ currentYear }} {{ restaurantName }}. All rights reserved.
              </p>
              <div class="flex gap-3">
                <a
                  v-if="dynamicContent?.content?.rsc_social_media_link_facebook"
                  :href="dynamicContent.content.rsc_social_media_link_facebook"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center transition-colors"
                  @mouseenter="($e) => ($e.currentTarget.style.backgroundColor = bgColor1)"
                  @mouseleave="($e) => ($e.currentTarget.style.backgroundColor = '')"
                >
                  <i class="ph-fill ph-facebook-logo text-lg"></i>
                </a>
                <a
                  v-if="dynamicContent?.content?.rsc_social_media_link_instagram"
                  :href="dynamicContent.content.rsc_social_media_link_instagram"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center transition-colors"
                  @mouseenter="($e) => ($e.currentTarget.style.backgroundColor = bgColor1)"
                  @mouseleave="($e) => ($e.currentTarget.style.backgroundColor = '')"
                >
                  <i class="ph-fill ph-instagram-logo text-lg"></i>
                </a>
                <a
                  v-if="dynamicContent?.content?.rsc_social_media_link_twitter"
                  :href="dynamicContent.content.rsc_social_media_link_twitter"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center transition-colors"
                  @mouseenter="($e) => ($e.currentTarget.style.backgroundColor = bgColor1)"
                  @mouseleave="($e) => ($e.currentTarget.style.backgroundColor = '')"
                >
                  <i class="ph-fill ph-twitter-logo text-lg"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Mobile Bottom Navigation -->
    <nav
      class="md:hidden fixed bottom-0 left-0 right-0 glass border-t border-slate-200 z-50 pb-safe"
    >
      <div class="flex justify-around items-center h-16 px-2">
        <a
          href="#home"
          class="flex flex-col items-center justify-center w-full h-full gap-1"
          :style="{ color: bgColor1 }"
        >
          <i class="ph-fill ph-house text-2xl"></i>
          <span class="text-[10px] font-semibold">Home</span>
        </a>
        <a
          href="#menu"
          class="flex flex-col items-center justify-center w-full h-full text-slate-400 transition-colors gap-1"
        >
          <i class="ph ph-squares-four text-2xl"></i>
          <span class="text-[10px] font-medium">Menu</span>
        </a>

        <div class="relative -top-5">
          <button
            type="button"
            class="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-floating border-4 border-[#f8fafc] active:scale-95 transition-transform relative"
            :style="{ backgroundColor: bgColor1 }"
            @click="toggleCart"
          >
            <i class="ph ph-shopping-cart-simple text-2xl"></i>
            <span
              v-if="cartCount > 0"
              class="absolute -top-1 -right-1 w-5 h-5 bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white"
            >
              {{ cartCount }}
            </span>
          </button>
        </div>

        <a
          href="#offers"
          class="flex flex-col items-center justify-center w-full h-full text-slate-400 transition-colors gap-1"
        >
          <i class="ph ph-tag text-2xl"></i>
          <span class="text-[10px] font-medium">Offers</span>
        </a>
        <router-link v-if="isLoggedIn"
          :to="{ name: 'my_orders' }"
          class="flex flex-col items-center justify-center w-full h-full text-slate-400 transition-colors gap-1"
        >
          <i class="ph ph-shopping-cart text-2xl"></i>
          <span class="text-[10px] font-medium">My Orders</span>
        </router-link>
        <router-link v-else
          :to="{ name: 'login' }"
          class="flex flex-col items-center justify-center w-full h-full text-slate-400 transition-colors gap-1"
        >
          <i class="ph ph-sign-in text-2xl"></i>
          <span class="text-[10px] font-medium">Login</span>
        </router-link>
      </div>
    </nav>

    <!-- Cart Overlay -->
    <transition name="list">
      <div v-if="isCartOpen" class="fixed inset-0 z-[100] flex flex-col justify-end">
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" @click="closeCart"></div>

        <div class="relative bg-white rounded-t-3xl h-[80vh] flex flex-col shadow-2xl">
          <div class="flex items-center justify-between p-5 border-b border-slate-100">
            <h2 class="text-xl font-bold text-slate-800">Your Order</h2>
            <button
              type="button"
              class="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500"
              @click="closeCart"
            >
              <i class="ph-bold ph-x"></i>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-5">
            <div
              v-if="cartCount === 0"
              class="flex flex-col items-center justify-center h-full text-slate-400"
            >
              <i class="ph-duotone ph-shopping-cart text-6xl mb-4 text-slate-200"></i>
              <p class="text-sm font-medium">Your cart is empty.</p>
              <button
                type="button"
                class="mt-4 font-semibold text-sm"
                :style="{ color: bgColor1 }"
                @click="
                  closeCart();
                  goToMenu();
                "
              >
                Start browsing
              </button>
            </div>

            <div v-else class="flex flex-col items-center justify-center h-full text-center px-6">
              <i
                class="ph-duotone ph-shopping-cart text-6xl mb-4"
                :style="{ color: bgColor1 }"
              ></i>
              <p class="text-base font-bold text-slate-800 mb-1">
                {{ cartCount }} item{{ cartCount === 1 ? '' : 's' }} in your cart
              </p>
              <p class="text-sm text-slate-500 mb-6">Continue to checkout to complete your order.</p>
              <button
                type="button"
                class="w-full max-w-xs py-4 text-white rounded-2xl font-bold shadow-floating active:scale-95 transition-all text-lg flex items-center justify-center gap-2"
                :style="{ backgroundColor: bgColor1 }"
                @click="
                  closeCart();
                  goToCart();
                "
              >
                View Cart <i class="ph-bold ph-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
@import '@/layouts/mobileAppSite/template3/css/responsive.css';
@import 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap';

.temp-w3 {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  background-color: #f8fafc;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.glass {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.4s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.z-\[100\] {
  z-index: 100;
}
</style>
